import { df, auth, functions } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'
import rules from '@/plugins/rules'
import state from '@/state'
import { clone } from '@/plugins/utils'

/**
 * Shared mixin for Item Dialog components (both Add and Edit)
 * Handles all business logic for item CRUD operations
 */
export default {
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data: () => ({
    visible: false,
    submitting: false,
    activeTab: 0,

    // Help modals
    showSellableHelp: false,

    // Price list location selector
    selectedPriceListLocation: null,

    // Validation tracking
    validationTriggered: false, // Only validate after first submit attempt
    tabErrors: {
      osnovni: false,
      jedinice: false,
      normativ: false,
      dobavljaci: false,
      slika: false,
      cjenik: false
    },

    // Price list data
    priceListData: {
      manualPriceInput: false,
      selectedCategories: [],
      selectedPrices: [], // [{currency, price, taxes, active}]

      // Non-warehouse
      selectedLocations: [],

      // Warehouse specific
      selectedSalesLocations: [],
      selectedOrgUnit: undefined,
      unitPrices: [], // [{unit_id, prices, rounding}]
      measureUnits: [],
      saleUnit: undefined,
      rounding: false,
      margins: [],
      normRows: []
    },
    priceListLoading: false,
    priceListExists: false,

    // Edit mode tracking
    itemId: null,
    defaultImg: '',
    originalData: null, // Stores original item data for comparison during updates

    // Item data
    name: undefined,
    code: undefined,
    barcode: undefined,
    kpdcode: undefined,
    type: undefined,
    storageUnit: undefined,
    sellingUnit: undefined,

    // Warehouse specific
    whStorageUnit: undefined,
    whSellingItem: true, // Default to true - items are sellable by default
    whDefaultSale: true, // Default checked (hidden from user)
    whDefaultStore: true, // Default checked (hidden from user)
    newUnits: [],
    existingUnits: [], // For edit mode
    baseUnitId: null, // Store base unit ID for price list
    unitIdMapping: {}, // Map old unit indices to new UUIDs for new items

    // Naknade
    naknade: [],
    showNaknade: false,

    // Norms
    norms: [],
    tableRows: [],

    // Suppliers
    suppliers: [],
    supplier: undefined,
    itemSuppliers: [],

    // Image
    itemImg: '',

    // Locations (warehouses)
    selectedLocations: [],
    locations: [],

    // Validation
    checking: false,
    isExistingCode: false,

    // Config
    warehouseModule: false,
    multiplier: 1,
    config: null,
    listeners: [],

    // Item types
    itemTypes: [],
    storageUnits: [],
    sellingUnits: [],

    // Tax groups
    taxGroups: [
      { text: 'Hrana - 13%', value: 'hrana' },
      { text: 'Piće - 25%', value: 'pice' },
      { text: 'Ostalo - 0%', value: 'ostalo-0' },
      { text: 'Ostalo - 5%', value: 'ostalo-5' },
      { text: 'Ostalo - 13%', value: 'ostalo-13' },
      { text: 'Ostalo - 25%', value: 'ostalo-25' }
    ],
    selectedTaxGroup: undefined,

    // Dropdown options
    naknadeTypes: [
      { label: 'Povratna naknada', value: 'povratna' }
    ],
    taxRates: [
      { label: 'PDV 0%', value: 0 },
      { label: 'PDV 5%', value: 5 },
      { label: 'PDV 13%', value: 13 },
      { label: 'PDV 25%', value: 25 }
    ],

    rules: {
      req: rules.req(),
      eq: rules.eq
    }
  }),

  computed: {
    isEditMode () {
      return this.itemId !== null
    },

    isInPointOfSale () {
      // Check if we're in cash register / POS context by checking the route
      const route = this.$route
      const pos = state.getPointOfSale()
      const isCashRegisterRoute = route && (route.path.includes('/cash-register') || route.path.includes('/point-of-sale'))
      // Only consider "in POS" if we're actually on a cash register route
      return isCashRegisterRoute && pos !== null && pos !== undefined
    },

    // Price list validation
    hasValidPriceListData () {
      const hasCategory = this.priceListData.selectedCategories.length > 0
      const hasPrice = this.priceListData.manualPriceInput ||
                       this.priceListData.selectedPrices.length > 0
      return hasCategory && hasPrice
    },

    // Field error computed properties
    nameError () {
      return this.tabErrors.osnovni && this.getFieldError('name')
    },
    typeError () {
      return this.tabErrors.osnovni && this.getFieldError('type')
    },
    taxGroupError () {
      return this.tabErrors.osnovni && this.getFieldError('selectedTaxGroup')
    },
    codeError () {
      return this.tabErrors.osnovni && this.getFieldError('code')
    },
    whStorageUnitError () {
      return this.tabErrors.osnovni && this.getFieldError('whStorageUnit')
    },
    storageUnitError () {
      return this.tabErrors.osnovni && this.getFieldError('storageUnit')
    },
    sellingUnitError () {
      return this.tabErrors.osnovni && this.getFieldError('sellingUnit')
    },
    cjenikValidationErrors () {
      if (!this.validationTriggered || !this.tabErrors.cjenik) {
        return []
      }
      return this.validateTab('cjenik')
    }
  },

  watch: {
    // Re-validate tab when user edits fields - only after first submit attempt
    name () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    type () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    selectedTaxGroup () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    code () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    whStorageUnit () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    storageUnit () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    sellingUnit () {
      if (this.validationTriggered) this.revalidateTab('osnovni')
    },
    newUnits: {
      handler () {
        if (this.validationTriggered) this.revalidateTab('jedinice')
      },
      deep: true
    },
    'priceListData.selectedCategories': {
      handler () {
        if (this.validationTriggered) this.revalidateTab('cjenik')
      },
      deep: true
    },
    'priceListData.selectedPrices': {
      handler () {
        if (this.validationTriggered) this.revalidateTab('cjenik')
      },
      deep: true
    },
    'priceListData.manualPriceInput' () {
      if (this.validationTriggered) this.revalidateTab('cjenik')
    }
  },

  mounted () {
    this.getMultiplier()

    if (state.getCurrentCompany().warehouse === 'module') {
      this.warehouseModule = true
    }

    this.rules.codeused = (val) => {
      return (
        !this.isExistingCode ||
        `Artikl sa šifrom ${val} već postoji.`
      )
    }

    this.itemTypes = [
      { name: this.$t('$vuetify.lager.itemTypes.goods'), value: 'GOODS' },
      { name: this.$t('$vuetify.lager.itemTypes.service'), value: 'SERVICE' }
    ]

    this.storageUnits = [
      { name: 'Komad', value: 'komad' },
      { name: 'Kilogram', value: 'kilogram' },
      { name: 'Litra', value: 'litra' },
      { name: 'Sat', value: 'sat' },
      { name: 'm2', value: 'm2' },
      { name: 'm', value: 'm' }
    ]

    this.sellingUnits = [
      { name: 'Komad', value: 'komad' },
      { name: 'Kilogram', value: 'kilogram' },
      { name: 'Litra', value: 'litra' },
      { name: 'Sat', value: 'sat' },
      { name: 'm2', value: 'm2' },
      { name: 'm', value: 'm' }
    ]
  },

  methods: {
    // Validation methods
    validateTab (tabName) {
      const errors = []

      switch (tabName) {
        case 'osnovni':
          if (!this.name || this.name.trim() === '') {
            errors.push('name')
          }
          if (!this.type) {
            errors.push('type')
          }
          // Tax group only required for warehouse module
          if (this.warehouseModule && !this.selectedTaxGroup) {
            errors.push('selectedTaxGroup')
          }
          if (this.isExistingCode) {
            errors.push('code')
          }
          if (this.warehouseModule) {
            if (!this.whStorageUnit || this.whStorageUnit.trim() === '') {
              errors.push('whStorageUnit')
            }
          } else {
            if (!this.storageUnit) {
              errors.push('storageUnit')
            }
            if (!this.sellingUnit) {
              errors.push('sellingUnit')
            }
          }
          break

        case 'jedinice':
          if (this.warehouseModule && this.newUnits.length > 0) {
            this.newUnits.forEach((unit, idx) => {
              if (!unit.customItemUnit || unit.customItemUnit.trim() === '') {
                errors.push(`unit_${idx}_name`)
              }
              if (!unit.customItemAmount || unit.customItemAmount.toString().trim() === '') {
                errors.push(`unit_${idx}_amount`)
              }
            })
          }
          break

        case 'normativ':
          // Normativ is optional, no validation errors needed
          break

        case 'dobavljaci':
          // Dobavljači are optional, no validation errors needed
          break

        case 'slika':
          // Image is optional, no validation errors needed
          break

        case 'cjenik': {
          // Cjenik is optional, but if user enters partial data, show validation
          const hasCategory = this.priceListData.selectedCategories.length > 0
          const hasPrice = this.priceListData.manualPriceInput ||
                           this.priceListData.selectedPrices.length > 0

          if (hasCategory && !hasPrice) {
            errors.push('prices_missing')
          }
          if (!hasCategory && hasPrice) {
            errors.push('categories_missing')
          }

          // Validate unit prices - if unit is active, it must have prices
          if (this.warehouseModule && this.priceListData.measureUnits && this.priceListData.measureUnits.length > 0) {
            this.priceListData.measureUnits.forEach((unit, idx) => {
              if (unit.active && (!unit.prices || unit.prices.length === 0)) {
                errors.push(`unit_${idx}_prices_missing`)
              }
            })
          }
          break
        }
      }

      return errors
    },

    validateAllTabs () {
      // Reset all errors
      this.tabErrors = {
        osnovni: false,
        jedinice: false,
        normativ: false,
        dobavljaci: false,
        slika: false,
        cjenik: false
      }

      // Validate each tab
      const tabNames = ['osnovni', 'jedinice', 'normativ', 'dobavljaci', 'slika', 'cjenik']
      let hasErrors = false

      tabNames.forEach(tabName => {
        const errors = this.validateTab(tabName)
        if (errors.length > 0) {
          this.tabErrors[tabName] = true
          hasErrors = true
        }
      })

      return !hasErrors
    },

    getFieldError (fieldName) {
      const errors = this.validateTab(this.getActiveTabName())
      return errors.includes(fieldName)
    },

    getActiveTabName () {
      const tabMap = this.warehouseModule
        ? ['osnovni', 'jedinice', 'normativ', 'naknade', 'dobavljaci', 'slika']
        : ['osnovni', 'naknade', 'slika']

      // Filter out tabs that don't exist for non-warehouse mode
      const filteredTabs = tabMap.filter(tab => {
        if (!this.warehouseModule) {
          return !['jedinice', 'normativ', 'dobavljaci'].includes(tab)
        }
        return true
      })

      return filteredTabs[this.activeTab] || 'osnovni'
    },

    clearTabError (tabName) {
      // Clear error for specific tab - called when user edits fields
      this.tabErrors[tabName] = false
    },

    revalidateTab (tabName) {
      // Re-validate specific tab and update its error status
      const errors = this.validateTab(tabName)
      // Only clear error if there are no validation errors
      this.tabErrors[tabName] = errors.length > 0
    },

    // Naknade methods
    addNaknada () {
      this.naknade.push({
        type: 'povratna',
        amount: 0,
        base: null,
        taxRate: 0,
        sellable: false
      })
    },

    removeNaknada (index) {
      this.naknade.splice(index, 1)
      if (this.naknade.length === 0) {
        this.showNaknade = false
      }
    },

    // Unit methods
    addMoreUnits () {
      const i = this.newUnits.length + 1
      this.newUnits.push({
        name: 'Mjerna jedinica',
        value: i,
        customItemUnit: '',
        customItemAmount: '',
        sellingItem: false,
        defaultSale: false,
        defaultStore: false
      })
    },

    removeUnit (index) {
      this.newUnits.splice(index, 1)
    },

    changeDefaultSaleCustom (value, index) {
      if (value) {
        if (this.newUnits.length > 0) {
          this.newUnits.forEach((_, key) => {
            if (key !== index) {
              this.newUnits[key].defaultSale = false
            }
          })
        }
        this.whDefaultSale = false
      }
    },

    changeDefaultStoreCustom (value, index) {
      if (value) {
        if (this.newUnits.length > 0) {
          this.newUnits.forEach((_, key) => {
            if (key !== index) {
              this.newUnits[key].defaultStore = false
            }
          })
        }
        this.whDefaultStore = false
      }
    },

    changeDefaultSale (value) {
      if (value) {
        if (this.newUnits.length > 0) {
          this.newUnits.forEach((_, key) => {
            this.newUnits[key].defaultSale = false
          })
        }
      }
    },

    changeDefaultStore (value) {
      if (value) {
        if (this.newUnits.length > 0) {
          this.newUnits.forEach((_, key) => {
            this.newUnits[key].defaultStore = false
          })
        }
      }
    },

    // Norm methods
    openAddNormForm () {
      if (this.$refs.addNorm) {
        // Pass current item, all items, and existing norms
        const currentItem = { id: this.itemId, name: this.name }
        this.$refs.addNorm.open(currentItem, [], this.norms)
      }
    },

    onAddNorm (data) {
      // AddNorm emits { norms: array, normType: string }
      if (data.norms) {
        this.norms = data.norms
        this.tableRows = this.norms.map(norm => ({
          name: norm.item?.name || norm.name || '',
          quantity: norm.quantity || 0
        }))

        // Force Vue to detect the change
        this.$forceUpdate()
      } else {
        // Fallback for direct array
        this.norms = data
        this.tableRows = this.norms.map(norm => ({
          name: norm.item?.name || norm.name || '',
          quantity: norm.quantity || 0
        }))
        this.$forceUpdate()
      }
    },

    onAddUnit (data) {
      console.log('Unit added:', data)
    },

    // Supplier methods
    openSupplier () {
      if (this.$refs.addCodes && this.supplier) {
        this.$refs.addCodes.open(this.supplier)
      }
    },

    onAddCodes (data) {
      const existingIndex = this.itemSuppliers.findIndex(
        s => s.id === data.id
      )
      if (existingIndex >= 0) {
        this.itemSuppliers[existingIndex] = data
      } else {
        this.itemSuppliers.push(data)
      }
    },

    removeSupplier (index) {
      this.itemSuppliers.splice(index, 1)
    },

    // Code availability check
    async checkCodeAvailability () {
      const code = this.code
      this.checking = true
      if (this.code !== undefined && this.code !== '' && this.code !== null) {
        let query = df
          .collection(`companies/${state.getCurrentCompany().id}/items`)
          .where('code', '==', code)
          .where('status', '==', 'OK')

        // Exclude current item in edit mode
        if (this.itemId) {
          query = query.where('id', '!=', this.itemId)
        }

        const isExistingCode = await query.get()
        this.isExistingCode = !isExistingCode.empty
      } else {
        this.isExistingCode = false
      }
      this.checking = false
    },

    // Image handling
    createImage (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.itemImg = e.target.result
      }
      reader.readAsDataURL(file)
    },

    onFileChange (file) {
      if (!file) {
        this.itemImg = ''
        return
      }
      if (file.size > 500000) {
        this.showMsgBox({
          text: 'Prevelika datoteka, maksimalna dopuštena veličina datoteke je 500 KB.',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        this.itemImg = ''
        if (this.$refs.imageUpl) {
          this.$refs.imageUpl.reset()
        }
        return
      }
      this.createImage(file)
    },

    // Dialog management
    open (item = null) {
      this.visible = true
      this.submitting = false

      if (item && item.id) {
        // Edit mode - load existing item data
        this.itemId = item.id
        // Save original data for comparison during updates
        this.originalData = clone(item)
        this.defaultImg = item.picture ? clone(item.picture) : ''
        this.name = item.name
        this.code = item.code
        this.barcode = item.barcode
        this.kpdcode = item.kpdcode
        this.itemImg = item.picture || ''
        this.selectedTaxGroup = item.tax_group

        // Find and set type from dropdown
        this.type = this.itemTypes.find(t => t.value === item.type) || this.itemTypes[0]

        // Handle units based on warehouse mode
        if (this.warehouseModule) {
          // Warehouse mode: sellingUnit is a string
          this.sellingUnit = item.selling_unit || ''

          // Load warehouse units
          if (item.warehouse_units && item.warehouse_units.units) {
            const baseUnitId = item.warehouse_units.base_unit
            const baseUnit = item.warehouse_units.units[baseUnitId]

            // Store base unit ID for price list payload
            this.baseUnitId = baseUnitId

            if (baseUnit) {
              // Load base unit data
              this.whStorageUnit = baseUnit.storage_unit
              this.whSellingItem = baseUnit.is_selling_item || false
              this.whDefaultSale = baseUnit.default_sale || false
              this.whDefaultStore = baseUnit.default_store || false
            }

            // Load custom units (non-base units)
            this.newUnits = []
            Object.keys(item.warehouse_units.units).forEach((unitId, index) => {
              if (unitId !== baseUnitId) {
                const unit = item.warehouse_units.units[unitId]
                const amount = unit.storage_amount / this.multiplier
                this.newUnits.push({
                  name: `Mjerna jedinica ${index}`,
                  value: index,
                  id: unitId, // Include the actual UUID
                  customItemUnit: unit.storage_unit,
                  customItemAmount: amount.toString().replace('.', ','),
                  sellingItem: unit.is_selling_item || false,
                  defaultSale: unit.default_sale || false,
                  defaultStore: unit.default_store || false
                })
              }
            })
          }
        } else {
          // Non-warehouse mode: units are dropdown selections (objects)
          this.storageUnit = this.storageUnits.find(u => u.value === item.storage_unit) || this.storageUnits[0]
          this.sellingUnit = this.sellingUnits.find(u => u.value === item.selling_unit) || this.sellingUnits[0]
        }

        // Load existing naknade if they exist
        if (item.naknade && typeof item.naknade === 'object') {
          this.naknade = []
          Object.keys(item.naknade).forEach(key => {
            const naknada = item.naknade[key]
            let taxRate = 0
            // Extract tax rate from taxes array if it exists
            if (naknada.taxes && naknada.taxes.length > 0) {
              taxRate = naknada.taxes[0].rate || 0
            }
            this.naknade.push({
              type: key,
              amount: (naknada.amount || 0) / 100, // Convert from cents
              base: naknada.base ? naknada.base / 100 : null,
              taxRate: taxRate,
              sellable: naknada.sellable !== undefined ? naknada.sellable : false
            })
          })
          if (this.naknade.length > 0) {
            this.showNaknade = true
          }
        }

        // Load existing norms if they exist (warehouse module only)
        if (this.warehouseModule && item.norm && item.norm.components) {
          this.norms = []
          this.tableRows = []
          Object.keys(item.norm.components).forEach(key => {
            const component = item.norm.components[key]
            const normItem = {
              name: component.item_name,
              quantity: component.quantity / this.multiplier,
              itemId: key,
              customUnitId: component.custom_unit_id || '',
              item: component
            }
            this.norms.push(normItem)
            this.tableRows.push({
              name: component.item_name,
              quantity: normItem.quantity
            })
          })
        }

        // Load existing price list data in edit mode - will be loaded after locations are loaded
        // this.$nextTick(() => {
        //   this.loadPriceListData(item.id)
        // })
      } else {
        // Add mode - set defaults
        this.itemId = null
        this.originalData = null
        this.type = this.itemTypes[0]
        if (this.warehouseModule) {
          // Warehouse mode: sellingUnit is a string
          this.sellingUnit = ''
        } else {
          // Non-warehouse mode: units are dropdown selections
          this.storageUnit = this.storageUnits[0]
          this.sellingUnit = this.sellingUnits[0]
        }
      }

      // Load suppliers
      df.collection(`companies/${state.getCurrentCompany().id}/associates`)
        .where('status', '==', 'OK')
        .where('type', '==', 'SUPPLIER')
        .get()
        .then((snapshot) => {
          this.suppliers = []
          snapshot.forEach((doc) => {
            this.suppliers.push({
              id: doc.id,
              ...doc.data()
            })
          })
        })
        .catch((error) => {
          console.error('Error loading suppliers:', error)
        })

      // Load user locations (warehouses)
      df.doc(`user_locations/${auth.currentUser.uid}.${state.getCurrentCompany().id}`)
        .get()
        .then((doc) => {
          if (doc.data() && doc.data().locations && Object.keys(doc.data().locations).length > 0) {
            this.locations = Object.keys(doc.data().locations)
              .map((key) => {
                return doc.data().locations[key]
              })
              .filter((location) => location.status === 'OK')

            this.selectedLocations = this.locations.map((location) => {
              return location.id
            })

            // Auto-select first location if not in POS
            if (!this.isInPointOfSale && this.locations.length > 0) {
              // Always select first location by default when not in POS
              this.selectedPriceListLocation = this.locations[0].id
              // Load price list data for the selected location if editing item
              if (this.itemId) {
                this.$nextTick(() => {
                  this.loadPriceListData(this.itemId)
                })
              }
            }
          }
        })
        .catch((error) => {
          console.error('Error loading locations:', error)
        })
    },

    close () {
      this.resetInputs()
    },

    resetPriceListData () {
      this.priceListData = {
        manualPriceInput: false,
        selectedCategories: [],
        selectedPrices: [],
        selectedLocations: [],
        selectedSalesLocations: [],
        selectedOrgUnit: undefined,
        unitPrices: [],
        measureUnits: [],
        saleUnit: undefined,
        rounding: false,
        margins: [],
        normRows: []
      }
      this.priceListLoading = false
      this.priceListExists = false
    },

    resetInputs () {
      // Basic fields
      this.itemImg = ''
      this.name = undefined
      this.code = undefined
      this.barcode = undefined
      this.kpdcode = undefined
      this.type = undefined
      this.storageUnit = undefined
      this.sellingUnit = undefined

      // Edit mode tracking
      this.itemId = null
      this.defaultImg = ''
      this.originalData = null
      this.visible = false

      // Warehouse specific
      this.whStorageUnit = undefined
      this.whSellingItem = true // Default to true - items are sellable by default
      this.whDefaultSale = true // Keep default as true
      this.whDefaultStore = true // Keep default as true
      this.newUnits = []
      this.existingUnits = []
      this.baseUnitId = null

      // Naknade
      this.naknade = []
      this.showNaknade = false

      // Norms
      this.norms = []
      this.tableRows = []

      // Suppliers
      this.supplier = undefined
      this.itemSuppliers = []

      // Locations
      this.selectedLocations = []
      this.selectedPriceListLocation = null

      // Tax group
      this.selectedTaxGroup = undefined

      // UI
      this.activeTab = 0

      // Reset validation errors
      this.validationTriggered = false
      this.tabErrors = {
        osnovni: false,
        jedinice: false,
        normativ: false,
        dobavljaci: false,
        slika: false,
        cjenik: false
      }

      // Reset price list data
      this.resetPriceListData()

      // Image upload ref
      if (this.$refs.imageUpl) {
        this.$refs.imageUpl.reset()
      }

      // Reset form validation
      if (this.$refs.addItemForm) {
        this.$refs.addItemForm.resetValidation()
      }
    },

    // Price list management
    buildPriceListPayload (itemId) {
      const categories = this.priceListData.selectedCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        picture: cat.picture
      }))

      const prices = this.priceListData.selectedPrices.reduce((acc, it) => {
        acc[it.currency] = {
          active: true,
          price: this.priceListData.manualPriceInput ? 0 : parseInt((it.price * 100).toFixed(2)),
          taxes: it.taxes.map(tax => ({ name: tax.name, rate: tax.rate }))
        }
        return acc
      }, {})

      if (this.warehouseModule) {
        // Build unit_prices from ALL units (base + selling units)
        const unitPrices = []

        // Add base unit first (if it has prices from selectedPrices)
        if (this.priceListData.selectedPrices && this.priceListData.selectedPrices.length > 0) {
          if (this.baseUnitId) {
            const basePricesMap = this.priceListData.selectedPrices.reduce((map, priceItem) => {
              return Object.assign(map, {
                [priceItem.currency]: {
                  active: true,
                  price: Math.round(priceItem.price * 100),
                  base: priceItem.base ? Math.round(priceItem.base * 100) : 0,
                  taxes: priceItem.taxes ? priceItem.taxes.map(tax => ({
                    name: tax.name,
                    rate: tax.rate
                  })) : []
                }
              })
            }, {})

            unitPrices.push({
              unit_id: String(this.baseUnitId),
              prices: basePricesMap
            })
          }
        }

        // Add selling units
        if (this.priceListData.measureUnits && this.priceListData.measureUnits.length > 0) {
          this.priceListData.measureUnits.forEach(unit => {
            // Get the actual UUID for this unit
            // For new items, use the mapping; for updates, use the unit.id directly
            let actualUnitId = unit.id
            if (!this.priceListExists && this.unitIdMapping[unit.id]) {
              actualUnitId = this.unitIdMapping[unit.id]
            }

            const pricesMap = unit.prices && unit.prices.length > 0
              ? unit.prices.reduce((map, priceItem) => {
                return Object.assign(map, {
                  [priceItem.currency]: {
                    active: unit.active || false,
                    price: Math.round(priceItem.price * 100),
                    base: priceItem.base ? Math.round(priceItem.base * 100) : 0,
                    taxes: priceItem.taxes ? priceItem.taxes.map(tax => ({
                      name: tax.name,
                      rate: tax.rate
                    })) : []
                  }
                })
              }, {})
              : {}

            unitPrices.push({
              unit_id: String(actualUnitId),
              prices: pricesMap
            })
          })
        }

        // Determine operation and resource_id
        const operation = this.priceListExists ? 'update' : 'set'
        const locationId = state.getPointOfSale()?.id || this.priceListData.selectedSalesLocations[0]

        const payload = {
          action: {
            operation: operation,
            entity: 'warehouse_module_pricelist_item',
            params: {
              company_id: state.getCurrentCompany().id,
              pricing_info: {
                active: true,
                categories: categories,
                manual_price_input: this.priceListData.manualPriceInput,
                unit_prices: unitPrices
              }
            }
          }
        }

        // Different params for SET vs UPDATE operations
        if (operation === 'set') {
          // For new items, use item_id and warehouses (similar to non-warehouse module)
          payload.action.params.item_id = itemId
          payload.action.params.warehouses = this.priceListData.selectedSalesLocations || [locationId]
          // Only add organizational_unit_id if it's selected
          if (this.priceListData.selectedOrgUnit) {
            payload.action.params.organizational_unit_id = this.priceListData.selectedOrgUnit
          }
        } else {
          // For updates, use resource_id and location_id
          payload.action.resource_id = itemId
          payload.action.params.location_id = locationId
          // Only add organizational_unit_id if it's selected
          if (this.priceListData.selectedOrgUnit) {
            payload.action.params.organizational_unit_id = this.priceListData.selectedOrgUnit
          }
        }

        return payload
      } else {
        return {
          action: {
            operation: 'set',
            entity: 'price_list',
            params: {
              warehouses: this.priceListData.selectedLocations,
              company_id: state.getCurrentCompany().id,
              item_id: itemId,
              pricing_info: {
                active: true,
                categories: categories,
                prices: prices,
                manual_price_input: this.priceListData.manualPriceInput
              }
            }
          }
        }
      }
    },

    async savePriceList (itemId) {
      const payload = this.buildPriceListPayload(itemId)
      const companyitemApi = functions.httpsCallable('companyitemapi')

      const result = await companyitemApi(payload)
      if (result.data.code !== 200) {
        throw new Error(result.data.message || 'Price list save failed')
      }
      return result
    },

    onPriceListLocationChange (locationId) {
      if (this.itemId) {
        this.loadPriceListData(this.itemId)
      }
    },

    async loadPriceListData (itemId) {
      this.priceListLoading = true
      try {
        const locationId = state.getPointOfSale()?.id || this.selectedPriceListLocation || this.selectedLocations[0]
        if (!locationId) {
          this.priceListExists = false
          this.priceListLoading = false
          return
        }

        const priceListDoc = await df
          .doc(`location_pricelist/${locationId}/items/${itemId}`)
          .get()

        if (priceListDoc.exists) {
          const data = priceListDoc.data()
          this.priceListExists = true

          // Map data to UI structure
          this.priceListData.selectedCategories = data.categories || []
          this.priceListData.manualPriceInput = data.manual_price_input || false

          // Map prices (convert from cents to decimal)
          if (data.prices) {
            this.priceListData.selectedPrices = Object.keys(data.prices).map(currency => ({
              currency: currency,
              price: data.prices[currency].price / 100,
              taxes: data.prices[currency].taxes || [],
              active: data.prices[currency].active
            }))
          }

          // Warehouse-specific data
          if (this.warehouseModule) {
            this.priceListData.selectedSalesLocations = data.warehouses || []
            this.priceListData.selectedOrgUnit = data.organizational_unit_id

            // Load base unit price from root level "prices" field
            if (data.prices) {
              this.priceListData.selectedPrices = Object.keys(data.prices).map(currency => ({
                currency: currency,
                price: data.prices[currency].price / 100, // Convert from cents
                base: data.prices[currency].base ? data.prices[currency].base / 100 : 0,
                taxes: data.prices[currency].taxes || [],
                active: data.prices[currency].active || false
              }))
            }

            // Load selling unit prices from warehouse_units.units[unitId].prices
            this.priceListData.unitPrices = []
            if (data.warehouse_units && data.warehouse_units.units) {
              Object.keys(data.warehouse_units.units).forEach(unitId => {
                const unit = data.warehouse_units.units[unitId]
                if (unit.prices) {
                  const pricesArray = Object.keys(unit.prices).map(currency => ({
                    currency: currency,
                    price: unit.prices[currency].price / 100,
                    base: unit.prices[currency].base ? unit.prices[currency].base / 100 : 0,
                    taxes: unit.prices[currency].taxes || [],
                    active: unit.prices[currency].active || false
                  }))

                  this.priceListData.unitPrices.push({
                    unit_id: unitId,
                    prices: pricesArray
                  })
                }
              })
            }
          } else {
            this.priceListData.selectedLocations = data.warehouses || []
          }
        } else {
          this.priceListExists = false
          this.resetPriceListData()
        }
      } catch (error) {
        console.error('Error loading price list data:', error)
        this.priceListExists = false
      } finally {
        this.priceListLoading = false
      }
    },

    // Payload building
    buildPayload (operation = 'set') {
      this.itemImg =
        this.itemImg && this.itemImg.split(',').length > 0
          ? this.itemImg.split(',')[1]
          : ''

      if (this.code == null) {
        this.code = ''
      }

      if (this.barcode == null) {
        this.barcode = ''
      }

      const payload = {
        action: {
          operation,
          entity: this.warehouseModule ? 'warehouse_module_item' : 'item',
          params: {}
        }
      }

      // For add operations, generate new ID and add all basic fields
      if (operation === 'set') {
        payload.action.params = {
          id: uuidv4(),
          name: this.name.toLowerCase().replace(/\s*$/, ''),
          type: this.type.value,
          company_id: state.getCurrentCompany().id,
          storage_unit: this.warehouseModule
            ? (this.whStorageUnit || '')
            : (this.storageUnit ? this.storageUnit.value : ''),
          selling_unit: this.warehouseModule
            ? (this.sellingUnit || '')
            : (this.sellingUnit ? this.sellingUnit.value : ''),
          warehouses: this.selectedLocations
        }

        // Only add code if it's not empty
        if (this.code !== '') {
          payload.action.params.code = this.code
        }

        // Add barcode if present
        if (this.barcode !== '') {
          payload.action.params.barcode = this.barcode
          payload.action.params.barcodes = [this.barcode]
        }

        // Add kpdcode if present
        if (this.kpdcode && this.kpdcode !== '') {
          payload.action.params.kpdcode = this.kpdcode
        }

        // Add tax_group if selected
        if (this.selectedTaxGroup) {
          payload.action.params.tax_group = this.selectedTaxGroup
        }
      }

      // For update operations, always send basic fields (from osnovni tab)
      if (operation === 'update' && this.itemId) {
        payload.action.resource_id = this.itemId

        // Always include all basic fields for updates (backend requires them)
        payload.action.params.name = this.name.toLowerCase().replace(/\s*$/, '')
        payload.action.params.type = this.type.value
        payload.action.params.company_id = state.getCurrentCompany().id
        payload.action.params.storage_unit = this.warehouseModule
          ? (this.whStorageUnit || '')
          : (this.storageUnit ? this.storageUnit.value : '')
        payload.action.params.selling_unit = this.warehouseModule
          ? (this.sellingUnit || '')
          : (this.sellingUnit ? this.sellingUnit.value : '')
        payload.action.params.warehouses = this.selectedLocations

        // Only add code if it's not empty
        if (this.code !== '') {
          payload.action.params.code = this.code
        }

        // Tax group is also on osnovni tab, always send it
        if (this.selectedTaxGroup) {
          payload.action.params.tax_group = this.selectedTaxGroup
        }

        // Barcode and kpdcode are on osnovni tab, only send if not empty
        if (this.barcode !== '') {
          payload.action.params.barcode = this.barcode
          payload.action.params.barcodes = [this.barcode]
        }
        if (this.kpdcode && this.kpdcode !== '') {
          payload.action.params.kpdcode = this.kpdcode
        }
      }

      // Handle image (separate tab - only send if changed)
      if (operation === 'set') {
        if (this.itemImg && this.itemImg !== '') {
          payload.action.params.picture = this.itemImg
        }
      } else if (operation === 'update') {
        // Only add picture if changed
        const originalPicture = this.originalData.picture || ''
        const currentPicture = this.itemImg || ''
        if (originalPicture !== currentPicture) {
          payload.action.params.picture = this.itemImg
        }
      }

      // Warehouse module specific
      if (this.warehouseModule) {
        // Build current warehouse units structure
        const units = []
        const mpUnits = new Map()

        // For updates, reuse existing base unit ID; for new items, generate new UUID
        const baseUnitUuid = (operation === 'update' && this.baseUnitId) ? this.baseUnitId : uuidv4()

        // For new items, create mapping from old IDs to new UUIDs
        if (operation === 'set') {
          this.unitIdMapping = {}
          this.baseUnitId = baseUnitUuid // Store for price list
        }

        let defaultStore = ''
        let defaultSale = ''

        const baseUnit = {
          id: baseUnitUuid,
          base_unit: true,
          storage_unit: this.whStorageUnit,
          storage_amount: 1 * this.multiplier,
          is_selling_item: this.whSellingItem ? this.whSellingItem : false,
          default_sale: this.whDefaultSale ? this.whDefaultSale : false,
          default_store: this.whDefaultStore ? this.whDefaultStore : false
        }

        units.push(baseUnit)
        mpUnits.set(baseUnitUuid, baseUnit)

        if (this.whDefaultSale !== undefined && this.whDefaultSale !== false) {
          defaultSale = baseUnitUuid
        }

        if (this.whDefaultStore !== undefined && this.whDefaultStore !== false) {
          defaultStore = baseUnitUuid
        }

        this.newUnits.forEach((unit, index) => {
          // Reuse existing UUID if available, otherwise generate new one
          const newUuid = unit.id || uuidv4()

          // For new items, map the old index/ID to the new UUID
          if (operation === 'set' && unit.value !== undefined) {
            this.unitIdMapping[unit.value] = newUuid
          }

          if (unit.defaultSale !== undefined && unit.defaultSale !== false) {
            defaultSale = newUuid
          }

          if (unit.defaultStore !== undefined && unit.defaultStore !== false) {
            defaultStore = newUuid
          }

          const temp = {
            id: newUuid,
            storage_unit: unit.customItemUnit,
            storage_amount: (parseFloat(unit.customItemAmount.replace(',', '.')) * this.multiplier),
            is_selling_item: unit.sellingItem ? unit.sellingItem : false,
            default_sale: unit.defaultSale ? unit.defaultSale : false,
            default_store: unit.defaultStore ? unit.defaultStore : false,
            base_unit: false
          }

          units.push(temp)
          mpUnits.set(newUuid, temp)
        })

        const whUnit = {
          base_unit: baseUnitUuid,
          default_sale: defaultSale,
          default_store: defaultStore,
          units: units
        }

        // For set operation, always add warehouse_units
        // For update operation, only add if changed
        if (operation === 'set') {
          payload.action.params.warehouse_units = whUnit
        } else if (operation === 'update') {
          // Compare warehouse_units with original
          const warehouseUnitsChanged = JSON.stringify(this.originalData.warehouse_units) !== JSON.stringify(whUnit)
          if (warehouseUnitsChanged) {
            payload.action.params.warehouse_units = whUnit
          }
        }

        // Handle norms (warehouse module only)
        if (this.norms && this.norms.length > 0) {
          const components = {}
          this.norms.forEach(norm => {
            const quantity = typeof norm.quantity === 'string'
              ? parseFloat(norm.quantity.replace(',', '.')) * this.multiplier
              : norm.quantity * this.multiplier

            components[norm.itemId] = {
              item_id: norm.itemId,
              item_name: norm.item?.name || norm.name || '',
              custom_unit_id: norm.customUnitId || '',
              custom_unit: !!(norm.customUnitId && norm.customUnitId !== ''),
              quantity: quantity
            }
          })

          const normData = {
            type: 'ON_SALE',
            components: components
          }

          // For set operation, always add norm
          // For update operation, only add if changed
          if (operation === 'set') {
            payload.action.params.norm = normData
          } else if (operation === 'update') {
            const originalNorm = this.originalData.norm || {}
            const normChanged = JSON.stringify(originalNorm) !== JSON.stringify(normData)
            if (normChanged) {
              payload.action.params.norm = normData
            }
          }
        } else if (operation === 'update' && this.originalData.norm) {
          // Norms were removed - send empty norm
          payload.action.params.norm = { type: 'ON_SALE', components: {} }
        }
      }

      // Handle naknade (separate tab - only send if changed)
      if (this.naknade && this.naknade.length > 0) {
        const naknadeObj = {}
        this.naknade.forEach(naknada => {
          if (naknada.type) {
            const naknadeData = {
              amount: Math.round((naknada.amount || 0) * 100), // Convert to cents
              taxes: [],
              sellable: naknada.sellable !== undefined ? naknada.sellable : false
            }

            if (naknada.base !== null && naknada.base !== undefined && naknada.base !== '') {
              naknadeData.base = Math.round(naknada.base * 100)
            }

            if (naknada.taxRate !== null && naknada.taxRate !== undefined) {
              naknadeData.taxes.push({
                name: `PDV ${naknada.taxRate}%`,
                rate: naknada.taxRate
              })
            }

            naknadeObj[naknada.type] = naknadeData
          }
        })

        if (Object.keys(naknadeObj).length > 0) {
          // For set operation, always add naknade
          // For update operation, only add if changed
          if (operation === 'set') {
            payload.action.params.naknade = naknadeObj
          } else if (operation === 'update') {
            const originalNaknade = this.originalData.naknade || {}
            const naknadeChanged = JSON.stringify(originalNaknade) !== JSON.stringify(naknadeObj)
            if (naknadeChanged) {
              payload.action.params.naknade = naknadeObj
            }
          }
        }
      } else if (operation === 'update' && this.originalData.naknade && Object.keys(this.originalData.naknade).length > 0) {
        // Naknade were removed - send empty object
        payload.action.params.naknade = {}
      }

      return payload
    },

    // Submit method
    submit () {
      // Enable validation on first submit attempt
      this.validationTriggered = true

      // First validate the form using Vuetify's built-in validation
      if (!this.$refs.addItemForm.validate()) {
        // Validate all tabs to highlight errors
        this.validateAllTabs()
        return
      }

      // Then validate all tabs to check for additional business logic errors
      const isValid = this.validateAllTabs()
      if (!isValid) {
        // Validation failed - error indicators on tabs are sufficient
        return
      }

      this.submitting = true

      const operation = this.isEditMode ? 'update' : 'set'
      const payload = this.buildPayload(operation)

      // Handle image - only process if it's changed
      if (this.itemImg && this.itemImg !== this.defaultImg) {
        if (this.itemImg.split(',').length > 0) {
          payload.action.params.picture = this.itemImg.split(',')[1]
        }
      } else if (!this.itemImg) {
        // Image was cleared
        delete payload.action.params.picture
      }

      const resp = payload.action.params
      var companyitemApi = functions.httpsCallable('companyitemapi')

      // Construct API call explicitly
      const apiCall = {
        action: {
          operation: payload.action.operation,
          entity: payload.action.entity,
          params: payload.action.params
        }
      }

      // Add resource_id for update operations
      if (payload.action.resource_id) {
        apiCall.action.resource_id = payload.action.resource_id
      }

      companyitemApi(apiCall)
        .then(async (result) => {
          if (result.data.code === 200) {
            const savedItemId = result.data.item?.id || this.itemId
            const action = this.isEditMode ? 'UPDATE' : 'ADD'

            // Emit refresh event immediately (keep existing behavior)
            this.$emit('success', { ...resp, ...{ action } })
            this.$emit('refresh', savedItemId)

            // Save to price list if user entered data
            if (this.hasValidPriceListData) {
              try {
                await this.savePriceList(savedItemId)
                this.showMsgBox({
                  text: 'Artikl kreiran i dodan na cjenik!',
                  actions: ['ok'],
                  color: 'success'
                })
              } catch (priceListError) {
                this.showMsgBox({
                  text: `Artikl je kreiran, ali nije dodan na cjenik: ${priceListError.message}`,
                  actions: ['ok'],
                  color: 'warning'
                })
              }
            }

            // Reset and close
            if (this.$refs.imageUpl) {
              this.$refs.imageUpl.reset()
            }
            if (this.$refs.addItemForm) {
              this.$refs.addItemForm.reset()
            }
            this.submitting = false
            this.resetInputs() // Reset all fields to defaults
            this.visible = false
          } else {
            this.showMsgBox({
              text: result.data.message ?? 'Dogodila se pogreška.',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
            this.submitting = false
          }
        })
        .catch((error) => {
          this.showMsgBox({
            text: error.message || 'Dogodila se pogreška.',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'error'
          })
          this.submitting = false
        })
    },

    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        const data = doc.data()
        if (data) {
          this.config = data
          if (data.item_amount && data.item_amount.decimal_multiplier !== undefined) {
            this.multiplier = data.item_amount.decimal_multiplier
          }
        }
      })
      this.listeners.push(listener)
    }
  }
}
